import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Chart, type ChartOptions } from '@highcharts/react';
import {
  StyledHeadedrTitle,
  StyledTsoIndicator,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationChart';
import {
  StyledChartHeader,
  StyledChartItem,
  StyledChartPlaceholder,
  StyledChartsWrapper,
  StyledTrendsChartActionBox,
  StyledTrendsChartActionBox2,
  StyledTrendsChartActionBox3,
  StyledTrendsChartActionsContainer,
  StyledTrendsChartContainer,
  StyledTrendsChartSubtitle,
  StyledTrendsChartTitle,
  StyledTrendsContainer,
} from '@pages/TrendIdentification/TrendIdentificationStyles';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetVoltageLevelGetByMeasurementDeviceQuery } from '@redux/apiSlices/voltageLevelApi';
import { useAppSelector } from '@redux/hooks';
import type { TrendChartsDataMap } from '@redux/slices/trendIdentificationSlice';
import {
  normalizeCategoryPath,
  selectSelectedCells,
  selectTrendChartsData,
} from '@redux/slices/trendIdentificationSlice';
import type { AxisLabelsFormatterContextObject } from 'highcharts/highcharts.src';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Option = {
  id: number;
  label: string;
};

type Criteria = {
  segmentValue: string;
  segmentLabel: string;
};

const TrendCharts = () => {
  const { t } = useTranslation();

  const trendsData: TrendChartsDataMap = useAppSelector(selectTrendChartsData);
  const selectedCells = useAppSelector(selectSelectedCells);

  const { data: measurementParametersRaw } = useGetMeasurementParameterGetAllQuery();
  const { data: measurementDevicesRaw } = useGetMeasurementDeviceGetAllQuery();
  const [triggerGetVoltageLevelByMeasurementDevice] = useLazyGetVoltageLevelGetByMeasurementDeviceQuery();

  const [colorLineSelectedOption, setColorLineSelectedOption] = useState<Option | null>();
  const [selectedGroupingCriteria, setSelectedGroupingCriteria] = useState<Criteria | null>(null);
  const [nominalVoltageByDeviceId, setNominalVoltageByDeviceId] = useState<Record<number, number>>({});

  const measurementParameters = measurementParametersRaw?.externalDtos ?? [];
  const measurementDevices = measurementDevicesRaw?.externalDtos ?? [];

  const deviceIds = Object.keys(trendsData).map(Number);

  const parameterIds = Array.from(
    new Set(deviceIds.flatMap((deviceId) => Object.keys(trendsData[deviceId] ?? {}).map(Number)))
  );

  const filteredDevices = measurementDevices.filter((devuce) => deviceIds.includes(devuce.id ?? Number.NaN));

  const selectedDeviceIds = selectedCells.map((cell) => cell.measurementDeviceId);
  const filteredParameters = measurementParameters.filter((parameter) =>
    parameterIds.includes(parameter.id ?? Number.NaN)
  );

  // ---- Clicka-clicka zone --------------------------------------------------

  const DEVICE_COLORS = ['#E91E63', '#FFC107', '#2196F3', '#4CAF50', '#9C27B0', '#FF5722'];

  const getDeviceColor = (deviceIds: number[]) => {
    const colorMap: Record<number, string> = {};
    let index = 0;

    for (const id of deviceIds) {
      colorMap[id] = DEVICE_COLORS[index % DEVICE_COLORS.length];
      index++;
    }

    return colorMap;
  };

  const VOLTAGE_COLORS = ['#03A9F4', '#8BC34A', '#FF9800', '#9E9E9E', '#673AB7'];

  const voltageColorMap = useMemo(() => {
    const map: Record<number, string> = {};
    let colorIndex = 0;

    const uniqueVoltages = Array.from(
      new Set(Object.values(nominalVoltageByDeviceId).filter((v): v is number => v != null))
    );

    for (const kv of uniqueVoltages) {
      map[kv] = VOLTAGE_COLORS[colorIndex % VOLTAGE_COLORS.length];
      colorIndex++;
    }

    return map;
  }, [nominalVoltageByDeviceId]);

  const lineColorDropdownOptions = [
    { id: 1, label: t('trendIdentification.chart.noSelection') },
    { id: 2, label: t('trendIdentification.chart.measurementDevice') },
    { id: 3, label: t('trendIdentification.chart.nominalVoltage') },
  ];

  const segmentedButtonOptions = [
    { segmentValue: 'device', segmentLabel: t('trendIdentification.chart.segment1') },
    { segmentValue: 'parameter', segmentLabel: t('trendIdentification.chart.segment2') },
  ];

  const handleColorLineOptionChange = (option: Option | null) => {
    setColorLineSelectedOption(option);
  };

  const handleGroupingCriteria = (values: string[]) => {
    const selected = segmentedButtonOptions.find((o) => o.segmentValue === values[0]) || null;
    setSelectedGroupingCriteria(selected);
  };

  useLayoutEffect(() => {
    setColorLineSelectedOption(lineColorDropdownOptions[0]);
    setSelectedGroupingCriteria(segmentedButtonOptions[0]);
  }, [t]);

  // BUILD device charts:
  const buildDeviceCharts = (devices: any[], params: any[], trendsData: TrendChartsDataMap) => {
    const charts = [];

    for (const device of devices) {
      const deviceId = device.id;
      const series = [];

      for (const param of params) {
        const entry = trendsData[deviceId]?.[param.id];
        if (!entry) continue;

        const data = [];

        for (const trendValue of entry.trendValues) {
          data.push([new Date(trendValue.weekStart).getTime(), trendValue.value]);
        }

        series.push({
          name: param.technicalName,
          data,
        });
      }

      charts.push({ deviceId, series });
    }

    return charts;
  };

  // BUILD grouped parameters chart:
  const buildGroupedParameterCharts = (
    devices: any[],
    parameters: any[],
    trendsData: any,
    lineColorMode: 1 | 2 | 3,
    nominalVoltageByDeviceId: Record<number, number | undefined>
  ) => {
    const groupedParams: Record<string, any[]> = {};

    for (const param of parameters) {
      const groupKey = normalizeCategoryPath(param.categoryPath);
      if (!groupedParams[groupKey]) {
        groupedParams[groupKey] = [];
      }
      groupedParams[groupKey].push(param);
    }

    const charts = [];

    for (const groupKey of Object.keys(groupedParams)) {
      const groupParams = groupedParams[groupKey];
      const series = [];

      for (const param of groupParams) {
        for (const device of devices) {
          const entry = trendsData[device.id]?.[param.id];
          if (!entry) continue;

          const parameterName = param.technicalName;
          const deviceName = device.name;
          const nominalKv = nominalVoltageByDeviceId[device.id];

          let seriesName = `${deviceName} – ${parameterName}`;
          if (lineColorMode === 3 && nominalKv != null) {
            seriesName = `${deviceName} (${nominalKv} kV) – ${parameterName}`;
          }

          const data = [];

          for (const trendValue of entry.trendValues) {
            data.push([new Date(trendValue.weekStart).getTime(), trendValue.value]);
          }

          series.push({
            name: seriesName,
            deviceId: device.id,
            data,
          });
        }
      }

      charts.push({
        groupKey,
        title: groupKey,
        series,
      });
    }

    return charts;
  };

  const baseOptions: ChartOptions = {
    chart: {
      type: 'line',
      height: 400,
      backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
      plotBackgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
    },
    title: { text: '', align: 'left', x: 0 },
    xAxis: {
      type: 'datetime',
      title: { text: 'Time' },
      labels: { format: '{value:%d.%m.%Y}' },
    },
    yAxis: { title: { text: '' } },
    tooltip: { shared: true, xDateFormat: '%d.%m.%Y' },
    credits: { enabled: false },
  };

  const options: ChartOptions = {
    ...baseOptions,
    xAxis: {
      ...baseOptions.xAxis,
      title: { text: '' },
      labels: {
        formatter: function (this: AxisLabelsFormatterContextObject) {
          const date = new Date(this.value as number);
          return new Intl.DateTimeFormat('de-DE', { month: 'short', year: '2-digit' }).format(date);
        },
      },
    },
    tooltip: {
      backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled,
      borderRadius: 4,
      style: {
        color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
        fontFamily: tsoPqTheme.typography.openSans,
        lineHeight: '1.125rem',
        fontSize: '0.75rem',
      },
      formatter: function (this: any) {
        const date = new Date(this.x as number);
        const formattedDate = date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

        const seriesInfo = this.points
          ?.map(
            (point: any) => `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: ${point.y}`
          )
          .join('');

        return `<b>${t('trendIdentification.chart.date')}</b> ${formattedDate}${seriesInfo}`;
      },
      shared: true,
      useHTML: true,
    },
  };

  const deviceCharts = useMemo(() => {
    return buildDeviceCharts(
      filteredDevices.filter((d) => selectedDeviceIds.includes(d.id ?? Number.NaN)),
      filteredParameters,
      trendsData
    ).filter((chart) => chart.series.length > 0);
  }, [filteredDevices, filteredParameters, trendsData, selectedDeviceIds]);

  const parameterCharts = useMemo(() => {
    return buildGroupedParameterCharts(
      filteredDevices,
      filteredParameters,
      trendsData,
      (colorLineSelectedOption?.id ?? 1) as 1 | 2 | 3,
      nominalVoltageByDeviceId
    ).filter((chart) => chart.series.length > 0);
  }, [filteredDevices, filteredParameters, trendsData, colorLineSelectedOption, nominalVoltageByDeviceId]);

  useEffect(() => {
    if (!filteredDevices.length) return;

    const fetchVoltages = async () => {
      for (const device of filteredDevices) {
        const deviceId = device.id;
        if (deviceId == null) continue;
        if (nominalVoltageByDeviceId[deviceId] !== undefined) continue;

        try {
          const response = await triggerGetVoltageLevelByMeasurementDevice(deviceId, true).unwrap();
          const kv = response.externalDto?.valueInKilovolt;

          if (kv != null) {
            setNominalVoltageByDeviceId((prev) => ({
              ...prev,
              [deviceId]: kv,
            }));
          }
        } catch (e) {
          console.error('Failed to fetch voltage level for device', deviceId, e);
        }
      }
    };

    fetchVoltages();
  }, [filteredDevices, triggerGetVoltageLevelByMeasurementDevice, nominalVoltageByDeviceId]);

  return (
    <StyledTrendsContainer>
      <StyledTrendsChartTitle>
        <TypographyTsoPq typographyType="sectionTitleMDBold">{t('trendIdentification.chart.title')}</TypographyTsoPq>
      </StyledTrendsChartTitle>
      <StyledTrendsChartSubtitle>
        <TypographyTsoPq typographyType="pageSubtitleSM">{t('trendIdentification.chart.subTitle')}</TypographyTsoPq>
      </StyledTrendsChartSubtitle>
      {selectedCells.length === 0 && (
        <StyledChartPlaceholder>
          <ImagePlaceholderTsoPq
            title={t('trendIdentification.chart.placeholder.title')}
            description={t('trendIdentification.chart.placeholder.description')}
          />
        </StyledChartPlaceholder>
      )}
      {parameterIds.length > 0 && (
        <StyledTrendsChartActionsContainer>
          <StyledTrendsChartActionBox />
          <StyledTrendsChartActionBox2>
            <SegmentedButtonTsoPq
              customWidth="27.1rem"
              options={segmentedButtonOptions}
              sizeVariant="m"
              defaultValue={segmentedButtonOptions[0]?.segmentValue}
              handleChange={handleGroupingCriteria}
            />
          </StyledTrendsChartActionBox2>
          {selectedGroupingCriteria?.segmentValue === 'parameter' ? (
            <StyledTrendsChartActionBox3>
              <TypographyTsoPq typographyType="labelMD">{t('trendIdentification.chart.lineColor')}</TypographyTsoPq>
              <SelectTsoPq
                label={''}
                options={lineColorDropdownOptions}
                customwidth="15rem"
                handleChange={handleColorLineOptionChange}
                value={colorLineSelectedOption}
              />
            </StyledTrendsChartActionBox3>
          ) : (
            <StyledTrendsChartActionBox3></StyledTrendsChartActionBox3>
          )}
        </StyledTrendsChartActionsContainer>
      )}
      {selectedGroupingCriteria?.segmentValue === 'device' && (
        <StyledChartsWrapper>
          {deviceCharts.map((chart: any) => {
            const deviceInfo = measurementDevices.find((d) => d.id === chart.deviceId);
            const title = deviceInfo?.name ?? ``;
            const tsoId = deviceInfo?.tsoId ?? Number.NaN;

            const deviceOptions: ChartOptions = {
              ...options,
              title: { text: '' },
              series: chart.series,
            };

            return (
              <StyledChartItem key={chart.deviceId} chartsCount={deviceCharts.length}>
                <StyledTrendsChartContainer>
                  <StyledChartHeader>
                    <StyledTsoIndicator tsoId={tsoId} />
                    <StyledHeadedrTitle>{title}</StyledHeadedrTitle>
                  </StyledChartHeader>
                  <Chart options={deviceOptions} />
                </StyledTrendsChartContainer>
              </StyledChartItem>
            );
          })}
        </StyledChartsWrapper>
      )}

      {selectedGroupingCriteria?.segmentValue === 'parameter' && (
        <StyledChartsWrapper>
          {parameterCharts.map((chart) => {
            const coloredSeries = chart.series.map((serie) => {
              if (!serie) return null;
              const deviceColorMap = getDeviceColor(selectedDeviceIds);
              if (colorLineSelectedOption?.id === 2) {
                return {
                  ...serie,
                  color: deviceColorMap[serie.deviceId],
                };
              }

              if (colorLineSelectedOption?.id === 3) {
                const kv = nominalVoltageByDeviceId[serie.deviceId];

                if (kv != null) {
                  return {
                    ...serie,
                    color: voltageColorMap[kv],
                  };
                }
              }
              return serie;
            });

            const paramOptions: ChartOptions = {
              ...options,
              title: { text: '' },
              series: coloredSeries,
            };

            return (
              <StyledChartItem key={chart.groupKey} chartsCount={parameterCharts.length}>
                <StyledTrendsChartContainer>
                  <StyledChartHeader>
                    <StyledHeadedrTitle>{t(chart.title)}</StyledHeadedrTitle>
                  </StyledChartHeader>
                  <Chart key={`${chart.groupKey}-${colorLineSelectedOption?.id ?? 'default'}`} options={paramOptions} />
                </StyledTrendsChartContainer>
              </StyledChartItem>
            );
          })}
        </StyledChartsWrapper>
      )}
    </StyledTrendsContainer>
  );
};

export default TrendCharts;
